<template>
  <div class="modelReg">
    <div class="modelReg__grid">
      <div class="modelReg__title">
        <h3>Создать новую категорию</h3>
      </div>

      <div v-if="loader" class="modelReg__alertLoading"></div>
      <div v-if="errorMessage" class="modelReg__alertError">{{ errorMessage }}</div>
      <div v-if="successMessage" class="modelReg__alertSuccess">{{ successMessage }}</div>

      <form class="modelReg__form" @submit.prevent="handleSubmit">
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Название категории *</label>
          <input
            v-model="form.name"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Введите название категории"
            @blur="v$.name.$touch"
          />
          <div v-if="v$.name.$error" class="text-red-500 text-sm">
            {{ v$.name.$errors[0]?.$message || 'Это поле обязательно' }}
          </div>
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Описание</label>
          <textarea
            v-model="form.description"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="3"
            placeholder="Введите описание категории"
          ></textarea>
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Имя для навигации (латиница)</label>
          <input
            v-model="form.to"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Введите имя для навигации"
          />
        </div>

        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700">Изображение</label>
          <div class="relative">
            <div class="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center border border-gray-300">
              <img
                v-if="form.previewUrl"
                :src="form.previewUrl"
                class="w-full h-full object-cover"
                alt="Preview"
              />
              <span v-else class="text-gray-500 text-center text-sm">Загрузить изображение</span>
            </div>
            <input
              type="file"
              accept="image/*"
              class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              @change="onImageChange"
            />
          </div>
        </div>

        <div class="modelReg__grid-btn">
          <UiButtonMain
            color="gray"
            variant="outline"
            label="Отмена"
            :disabled="loader"
            @click="closeModal"
          />
          <UiButtonMain
            color="sky"
            label="Создать категорию"
            :disabled="loader || v$.$invalid"
            type="submit"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { consola } from 'consola';
import { apiAdminCategory } from '~/fsd-pages/admin/api';

const emit = defineEmits(['close', 'category-created', 'update:size']);

const logger = consola.withTag('AddCategoryModal');

const loader = ref(false);
const errorMessage = ref<string | null>(null);
const successMessage = ref<string | null>(null);

// Форма
const form = reactive({
  name: '',
  description: '',
  to: '',
  previewUrl: undefined as string | undefined,
  _file: undefined as File | undefined,
});

// Валидация
const rules = {
  name: { required },
};

const v$ = useVuelidate(rules, form);

const onImageChange = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  form.previewUrl = URL.createObjectURL(file);
  form._file = file;
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  if (!form.name || !form.name.trim()) {
    errorMessage.value = 'Название категории обязательно!';
    return;
  }

  loader.value = true;
  errorMessage.value = null;
  successMessage.value = null;

  try {
    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('description', form.description ?? '');
    formData.append('to', form.to ?? '');

    if (form._file) {
      formData.append('image', form._file);
    }

    // Для создания категории с изображением нужно использовать другой API или изменить логику
    // Пока что создаем без изображения
    const categoryData = {
      name: form.name,
      description: form.description ?? '',
      to: form.to ?? '',
    };

    await apiAdminCategory.create(categoryData);

    successMessage.value = 'Категория успешно создана!';

    // Сброс формы
    Object.assign(form, {
      name: '',
      description: '',
      to: '',
      previewUrl: undefined,
      _file: undefined,
    });

    v$.value.$reset();

    // Уведомляем родительский компонент
    emit('category-created');

    // Закрываем модал через 1 секунду
    setTimeout(() => {
      closeModal();
    }, 1000);

  } catch (error: any) {
    logger.error('Ошибка создания категории:', error);
    errorMessage.value = error?.response?.data?.message || error?.message || 'Ошибка сервера';
  } finally {
    loader.value = false;
  }
};

const closeModal = () => {
  emit('close');
};

onMounted(() => {
  emit('update:size', 'l');
});
</script>

<style lang="scss" scoped>
.modelReg {
  &__grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__title {
    text-align: center;
    margin-bottom: 1rem;

    h3 {
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--text-primary);
      margin: 0;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__grid-btn {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;

    @media (max-width: 640px) {
      flex-direction: column;
    }
  }

  &__alertLoading {
    background: var(--info-bg);
    color: var(--info-color);
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
  }

  &__alertError {
    background: var(--error-bg);
    color: var(--error-color);
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
  }

  &__alertSuccess {
    background: var(--success-bg);
    color: var(--success-color);
    padding: 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
  }
}
</style>